import {
  ChangeEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
  useLayoutEffect
} from 'react';

/**
 * Convenience function for extracting an array of every separator.
 * Adjacent separators will be combined as a single separator.
 */
const getSeparators = (formatPattern: string) =>
  formatPattern.split(/[^\W_]+/g).filter(Boolean);

/**
 * Convenience function for stripping all formatting chars from a
 * formatted string.
 */
const stripFormatting = (value: string): string => {
  return value.replace(/[\W_]+/g, '');
};

/**
 * Convenience function for getting the character to the left of the caret
 */
const prevCharacter = (position: number, pattern: string) =>
  pattern.substring(position - 1, position);

/**
 * Convenience function for getting the character to the right of the caret
 */
const nextCharacter = (position: number, pattern: string) =>
  pattern.substring(position, position + 1);

/**
 * Finds the next safe caret position for input.
 * Safe positions skip over formatting characters to make sure that edits
 * happen at the right location.
 */
const findNextSafePosition = (position: number, pattern: string): number => {
  const separators = getSeparators(pattern).join('');
  let offset = 0;
  while (offset < pattern.length) {
    const next = nextCharacter(position + offset, pattern);

    if (!separators.includes(next)) {
      break;
    }

    offset++;
  }
  return offset;
};

/**
 * Finds the previous safe caret position for input.
 * Safe positions skip over formatting characters to make sure that edits
 * happen at the right location.
 */
const findPrevSafePosition = (position: number, pattern: string): number => {
  const separators = getSeparators(pattern).join('');
  let offset = 0;
  while (offset < pattern.length) {
    const prev = prevCharacter(position - offset, pattern);

    if (!separators.includes(prev)) {
      break;
    }

    offset++;
  }
  return offset;
};

/**
 * Function that takes a pattern and a value, parses the pattern,
 * and outputs a value that has been formatted to match the pattern.
 *
 * It works by splitting the pattern string into blocks anywhere a
 * non-alphanumeric character appears. It then runs those blocks
 * through a reducer, joining the alphanumeric blocks and appending
 * the appropriate separators after each block.
 *
 * This is a fork of format-string-by-pattern. It was forked, because
 * that package only supports formatting behind the caret, our design
 * spec requires formatting ahead of the caret.
 *
 * Input formatting logic:
 *
 * [input] > [result]
 *
 *         > [^             ]
 *      5  > [(5^           ]
 *      5  > [(55^          ]
 *      5  > [(555) ^       ]
 *      1  > [(555) 1^      ]
 *      2  > [(555) 12^     ]
 *      3  > [(555) 123-^   ]
 *      -  > [(555) 123-^   ]
 *      9  > [(555) 123-9^  ]
 *      8  > [(555) 123-98^ ]
 *      7  > [(555) 123-987^]
 *      6  > [(555) 123-9876]
 */
export const formatWithPattern = (
  value: string | number | undefined,
  pattern: string | undefined
): string | undefined => {
  if (!value || !pattern) return undefined;
  const cleanValue = stripFormatting(String(value));
  const blockSizes = pattern
    .split(/[\W_]+/g)
    .filter(Boolean)
    .map((b) => b.length);
  const separators = getSeparators(pattern);
  const [firstSeparator] = separators;
  // boolean that indicates whether the first character is a separator
  const startsWithSeparator = pattern.startsWith(firstSeparator);
  const indexOffset = startsWithSeparator ? 1 : 0;

  const afterReduce = blockSizes.reduce(
    (acc, _, index) => {
      const curBlockSize = blockSizes[index];

      const beforeSlice = acc.value.slice(0, curBlockSize);
      const afterSlice = acc.value.slice(curBlockSize);

      // character to prefix the current block with
      const prefix = index === 0 && startsWithSeparator ? firstSeparator : '';
      // character to suffix the current block with
      const suffix =
        beforeSlice.length === curBlockSize
          ? separators[index + indexOffset]
          : '';

      const nextResult = beforeSlice
        ? acc.result.concat(prefix, beforeSlice, suffix)
        : acc.result;

      return {
        result: nextResult,
        value: afterSlice
      };
    },
    {
      result: '',
      value: cleanValue
    }
  );

  return afterReduce.result.slice(0, pattern.length);
};

/**
 * This is the array returned from the useFormatter hook
 */
//export type FormatterHook = [Formatter, string | undefined];
export interface FormatterHook {
  setup: Formatter;
  value: string | undefined;
  sync: (v: string, config?: SyncConfig) => void;
}

type PreFormat = (value: string) => string;
type PostFormat = (value: string) => string;
type ShouldFormat = (e: ChangeEvent<HTMLInputElement>) => boolean;
type AfterFormat = (e: ChangeEvent<HTMLInputElement>) => void;
type Formatter = (
  config: FormatterConfig
) => (e: ChangeEvent<HTMLInputElement>) => void;

export interface FormatterConfig {
  /**
   * shouldFormat is run before any formatting occurs. If you return false,
   * formatting is cancelled.
   */
  shouldFormat?: ShouldFormat;
  /**
   * Fires after a successful formatting change
   */
  onChange?: AfterFormat;
  /**
   * preFormat allows you to mutate the string before formatting it
   */
  preFormat?: PreFormat;
  /**
   * postFormat allows you to mutate the string after formatting it. This is
   * useful for applying internal formatting for your form fields. i.e. storing
   * a date format that is different from the format displayed in the ui.
   */
  postFormat?: PostFormat;
}

type SyncConfig = Pick<FormatterConfig, 'preFormat'>;

/**
 * The formatter hook acts as a middleware between the native
 * HTMLInputElement and final form's input field. Although, it
 * does not depend on final form and will work anywhere you're
 * using an HTMLInputElement. When the formatter is passing the
 * data to the nextOnChange event, the raw unformatted value is
 * passed instead of the formatted value.
 *
 * Since the data flow is always moving from Input>Formatter>FF,
 * if the value being returned by final-form differs from the value stored
 * in the formatter, we can assume that final-form has mutated the value.
 * This usually happens when a form is reset or we're loading an initial value.
 * In this case, we will temporarily reverse the flow to use final-form's value
 *
 * After the formatted value is returned back to the HTMLInputElement,
 * the formatter hook launches a side-effect that corrects the caret
 * location, shifting past any new separator characters.
 */
export const useFormatter = (
  input?: HTMLInputElement,
  formatPattern?: string
): FormatterHook => {
  const [value, setValue] = useState<string>();
  const valueRef = useRef<string>();
  const lastValueRef = useRef<string>();
  const caretPosition = useRef<number>();
  const selection = useRef<[number, number]>();

  /**
   * Convenience function for updating both the state and valueRef.
   * A valueRef is needed so that the entire formatting pipeline has access
   * to the exact same value. setState functions are async and there's no
   * guarantee that the value will be updated by the time the next callback
   * is executed.
   */
  const updateValue = useCallback(
    (nextValue: string | undefined) => {
      const value = formatWithPattern(nextValue, formatPattern);
      valueRef.current = value;
      setValue(value);
    },
    [formatPattern]
  );

  /**
   * This keydown handler is added for correcting backspace deletions.
   * Honestly not really a fan of this solution, but unsure what a better
   * method for handling this would be.
   *
   * The problem: when your input looks like: `(555) 123-4^` and you backspace,
   * the input should look like this `(555) 123-^`. When you backspace again,
   * it should look like this: `(555) 12^`. We skipped over the dash and deleted
   * the next number. In order to accomplish this, this handler is listening for
   * keydown events and observing what character is to the left of the caret.
   * If there is a separator to the left of the caret, it will move the caret to
   * the left of the separator. Since keydown events fire before input mutations
   * occur, the actual backspace event will delete the number instead of the
   * separator.
   *
   * This same problem exists for the delete key, same solution but inverted.
   * This has been tested on all major browsers and on some mobile devices.
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!formatPattern) return;
      const target = e.target as HTMLInputElement;

      // if we have text highlighted, save the range and exit early.
      if (target.selectionStart !== target.selectionEnd) {
        selection.current = [
          target.selectionStart || 0,
          target.selectionEnd || 0
        ];
        return;
      } else {
        // if we don't have anything selected, clear the selection ref
        // before processing the keydown event.
        selection.current = undefined;
      }

      if (e.key === 'Backspace') {
        const pos = target.selectionStart || 0;
        const offset = findPrevSafePosition(pos, formatPattern);
        target.setSelectionRange(pos - offset, pos - offset);
      } else if (e.key === 'Delete') {
        const pos = target.selectionStart || 0;
        const offset = findNextSafePosition(pos, formatPattern);
        target.setSelectionRange(pos + offset, pos + offset);
      }
    },
    [formatPattern]
  );

  // setup keydown listeners for backspace/delete
  useEffect(() => {
    if (input) {
      input.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (input) {
        input.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [handleKeyDown, input]);

  // this is an effect that runs after every successful value update.
  // It's purpose is to adjust the caret location to prevent it from
  // jumping to the end of the input after formatting is applied.
  useLayoutEffect(() => {
    // if this isn't our first value entry, we need to adjust the caret
    if (
      lastValueRef.current &&
      input &&
      caretPosition.current &&
      formatPattern
    ) {
      // check that the caret is not at the end of the input. If the caret
      // is at the end, we can just leave it there.
      if (input.value.length !== caretPosition.current) {
        // Since we're not at the end of the input, we have to calculate
        // whether the caret needs offset based on formatting updates.
        //
        // In the example below, the caret needs to be offset to the right
        // when a new value is added:
        //
        // [(000) 00^0-00  ]
        // [(000) 001^-000 ]
        //
        // So we look forward in the pattern to see how many formatting
        // characters we need to shift past (in this case, it's only one)
        //
        // [(000) 001-^000 ]

        let offset = 0;

        // if we have an active selection, calculate caret offset based on
        // the start of the selection range.
        if (selection.current) {
          offset = findNextSafePosition(selection.current[0], formatPattern);
        } else {
          // no selection is made, use the onChange caret location instead.
          offset = findNextSafePosition(caretPosition.current, formatPattern);
        }

        // apply the updated caret location to the input
        input.setSelectionRange(
          caretPosition.current + offset,
          caretPosition.current + offset
        );
      }
    }

    // save a ref of the last successful update
    lastValueRef.current = valueRef.current;
  }, [value, formatPattern, input]);

  /**
   * Setup returns a `Formatter` that can be used on the input's onChange event.
   * The before and after arguments are functions that get called before and
   * after the formatter runs. The before function is capable of cancelling the
   * formatting event, by returninig a non-true value. Both the before and after
   * functions will receive modified versions of the onChange event that have
   * the value stripped of all formatting. This is for easier validation.
   */
  const setup: Formatter = useCallback(
    ({ shouldFormat, onChange, preFormat, postFormat }: FormatterConfig) => (
      e: ChangeEvent<HTMLInputElement>
    ) => {
      // save a ref of the current caret position
      caretPosition.current = e.target.selectionStart || 0;
      const nextValue = e.target.value;
      e.target.value = stripFormatting(e.target.value || '');

      if (shouldFormat && !shouldFormat(e)) {
        return;
      }

      if (preFormat) {
        e.target.value = preFormat(e.target.value);
      }

      updateValue(nextValue);

      if (postFormat) {
        e.target.value = postFormat(e.target.value);
      }

      onChange && onChange(e);
    },
    [updateValue]
  );

  /**
   * Function for syncing an externally controlled value with the formatter's
   * state. For use with final-form, you'll want to combine this with FormSpy.
   *
   * This allows changes made externally by the form controller to sync with
   * the internal formatter state. Useful when combined with final-form, since
   * final-form will mutate the input when resetting or setting initialValues.
   *
   * Example:
   *
   * ```
   * <FormSpy
   *   subscription={{ values: true }}
   *   onChange={({ values }) => formatter.sync(values.fieldName)}
   * />
   * ```
   */
  const sync = useCallback(
    (nextValue: string, { preFormat }: SyncConfig = {}) => {
      const stripped = stripFormatting(valueRef.current || '');

      // if the incoming value is out of sync with our raw input value,
      if (nextValue !== stripped) {
        updateValue(preFormat ? preFormat(nextValue) : nextValue);
      }
    },
    [updateValue]
  );

  // memoize the formatter response to prevent unnecessary renders when setting
  // the formatter as a hook dependency.
  const formatter = useMemo(() => ({ setup, value, sync }), [
    setup,
    value,
    sync
  ]);

  return formatter;
};
